/**
 * Global error capture for every Aven frontend (Vue via `setupVueErrorHandler`,
 * React via direct call): `window.onerror` + `unhandledrejection` + the
 * stale-chunk auto-reload guard. Idempotent per page.
 */

export interface LogSink {
    info: (template: string, args?: Record<string, unknown>) => void
    warn: (template: string, args?: Record<string, unknown>) => void
    error: (template: string, args?: Record<string, unknown>) => void
}

// Network-noise messages are demoted to warn so they don't page on flaky transport.
export const DEMOTED_ERROR_MESSAGES: RegExp[] = [
    /Network Error/gi, // A temporary network problem that is /usually/ on the client's side / mobile networks / etc
    /Unexpected token/gi, // This is caused when we deploy while someone is on the site and they get a 404 from the missing file
    /SyntaxError/gi, // This pops up on offbrand browsers, esp ones that inject their own JS
    /Loading.*chunk.*failed./gi, // This is also caused by a bad client-side cache and/or deploy
    /Request aborted/gi, // This is caused when an in-flight HTTP request is cancelled (while reloading, changing pages, etc)
    /Script error\./gi, // This is caused when a script loaded from a third-party fails for any reason. Browser does not provide any details even about the third-party origin (see: https://sentry.io/answers/script-error/)
]

// Chunk-reload attempts tracked in a URL param (not sessionStorage) so they survive the
// reload and work in iframe / private-browsing contexts where sessionStorage may throw.
const CHUNK_RELOAD_PARAM = '_chunkReload'
const MAX_CHUNK_RELOAD_ATTEMPTS = 2
const CHUNK_LOAD_FAILED_REGEX = /Loading.*chunk.*failed./i

// How long after app code starts a full-page navigation we treat chunk failures as fallout of it.
const NAVIGATION_PENDING_WINDOW_MS = 10_000

let installed = false
let navigationPendingSince: number | null = null

/**
 * Call right before app code starts a full-page navigation (`location.href = ...`, `location.reload()`).
 * WebKit cancels the departing page's in-flight script loads and fires their error events while the old
 * page is still alive, so webpack chunk promises reject mid-navigation. Auto-reloading on those failures
 * would replace the pending navigation with a reload of the page being left.
 */
export function markAppNavigationPending(): void {
    navigationPendingSince = Date.now()
}

export function isAppNavigationPending(): boolean {
    return navigationPendingSince !== null && Date.now() - navigationPendingSince < NAVIGATION_PENDING_WINDOW_MS
}

// Webpack sets `request` on ChunkLoadError; its message also ends with "(error|timeout|missing: <url>)".
function getChunkRequestUrl(reason: { request?: unknown } | undefined, message: string): string | undefined {
    if (typeof reason?.request === 'string' && reason.request.length > 0) {
        return reason.request
    }
    return /\((?:[a-z]+: )?([^\s)]+)\)\s*$/i.exec(message)?.[1]
}

/**
 * Only chunks served from our own origin can be fixed by reloading the page. Third-party scripts with their own
 * webpack runtime (e.g. Zendesk's snippet.js lazily loading static.zdassets.com chunks) also throw
 * "Loading chunk N failed." and must not trigger a reload. Unattributable failures keep the legacy behavior.
 */
function isOwnChunk(requestUrl: string | undefined): boolean {
    if (!requestUrl) {
        return true
    }
    try {
        return new URL(requestUrl, window.location.href).origin === new URL(window.location.href).origin
    } catch {
        return true
    }
}

function readChunkReloadCount(): number {
    try {
        const raw = new URLSearchParams(window.location.search).get(CHUNK_RELOAD_PARAM)
        const n = raw ? parseInt(raw, 10) : 0
        return Number.isFinite(n) && n >= 0 ? n : 0
    } catch {
        return 0
    }
}

function redirectWithIncrementedChunkReloadCount(currentCount: number): void {
    try {
        const url = new URL(window.location.href)
        url.searchParams.set(CHUNK_RELOAD_PARAM, String(currentCount + 1))
        window.location.replace(url.toString())
    } catch {
        window.location.reload()
    }
}

/**
 * Wire `window.onerror` + `unhandledrejection` into `log`. Chunk-load failures
 * trigger a rate-limited auto-reload; network-noise messages are demoted to warn.
 * Idempotent; call once at boot (or let `createLogger().setupVueErrorHandler`
 * do it for you on Vue apps).
 */
export function installGlobalErrorHandlers(log: LogSink): void {
    if (typeof window === 'undefined' || installed) {
        return
    }
    installed = true

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason
        const reasonMessage = String(reason?.message ?? '')
        if (CHUNK_LOAD_FAILED_REGEX.test(reasonMessage)) {
            if (isAppNavigationPending()) {
                log.warn('Ignoring chunk load failure while a page navigation is pending', { error: reason })
                return
            }
            if (!isOwnChunk(getChunkRequestUrl(reason, reasonMessage))) {
                log.warn('Ignoring chunk load failure from a third-party script', { error: reason })
                return
            }
            const reloadCount = readChunkReloadCount()
            if (reloadCount < MAX_CHUNK_RELOAD_ATTEMPTS) {
                log.info('Reloading page to fix stale chunk error', { priorAttempts: reloadCount })
                redirectWithIncrementedChunkReloadCount(reloadCount)
                return
            }
            log.error('Chunk load failure persisting after auto-reload attempts; surfacing instead of looping', {
                priorAttempts: reloadCount,
                error: reason,
            })
            return
        }
        if (DEMOTED_ERROR_MESSAGES.some((re) => re.test(String(reason)))) {
            log.warn('window.onunhandledrejection error', { error: reason })
        } else {
            log.error('window.onunhandledrejection error', { error: reason })
        }
    })

    window.addEventListener('error', (event: ErrorEvent) => {
        const { message, filename, lineno, colno, error } = event
        if (DEMOTED_ERROR_MESSAGES.some((re) => re.test(String(message)))) {
            log.warn('window.onerror error', { message, filename, lineno, colno, error })
        } else {
            log.error('window.onerror error', { message, filename, lineno, colno, error })
        }
    })
}
